import { toPng } from "html-to-image";

export type ShareReceiptResult =
  | "shared"
  | "downloaded"
  | "cancelled"
  | "skipped";

const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
  const response = await fetch(dataUrl);
  return response.blob();
};

export const isAbortError = (err: unknown): boolean =>
  typeof err === "object" &&
  err !== null &&
  "name" in err &&
  (err as { name: string }).name === "AbortError";

/** Rasterize a laid-out node. Lock box so % padding does not reflow the clone.
 * Opaque white: `.summary-section` has no fill; without this the PNG is
 * transparent and dark-UI viewers show black behind grey type. */
export const captureElementPng = async (
  element: HTMLElement,
  pixelRatio = 2
): Promise<Blob> => {
  const width = Math.ceil(element.getBoundingClientRect().width) || 1;
  const height = Math.ceil(element.getBoundingClientRect().height) || 1;

  const dataUrl = await toPng(element, {
    pixelRatio,
    cacheBust: true,
    backgroundColor: "#ffffff",
    width,
    height,
    canvasWidth: Math.round(width * pixelRatio),
    canvasHeight: Math.round(height * pixelRatio),
    style: {
      width: `${width}px`,
      height: `${height}px`,
      transform: "none",
      opacity: "1",
      overflow: "hidden",
      backgroundColor: "#ffffff",
    },
  });

  return dataUrlToBlob(dataUrl);
};

/** Phone/tablet share sheet. Desktop Chrome may expose canShare; skip it. */
export const isMobileShareSurface = (): boolean => {
  const matchMedia = globalThis.matchMedia;
  if (typeof matchMedia !== "function") return false;
  try {
    return matchMedia("(hover: none) and (pointer: coarse)").matches;
  } catch {
    return false;
  }
};

export const canShareFilesNatively = (
  mediaType = "application/pdf"
): boolean => {
  if (typeof navigator.canShare !== "function") return false;
  try {
    const probe = new File([], "probe", { type: mediaType });
    return navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
};

export const shouldOfferNativeShare = (
  mediaType = "application/pdf"
): boolean => isMobileShareSurface() && canShareFilesNatively(mediaType);

export const downloadBlob = (blob: Blob, fileName: string): void => {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = fileName;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(href);
};

const canShareFiles = (file: File): boolean =>
  typeof navigator.canShare === "function" && navigator.canShare({ files: [file] });

export const shareReceiptImage = async ({
  element,
  fileName,
  title,
}: {
  element: HTMLElement | null;
  fileName: string;
  title: string;
}): Promise<ShareReceiptResult> => {
  if (!element) return "skipped";

  const blob = await captureElementPng(element);
  const file = new File([blob], fileName, { type: "image/png" });

  if (canShareFiles(file) && typeof navigator.share === "function") {
    try {
      await navigator.share({ files: [file], title });
      return "shared";
    } catch (err) {
      if (isAbortError(err)) return "cancelled";
    }
  }

  downloadBlob(blob, fileName);
  return "downloaded";
};

/** Share via the OS sheet on mobile; download on desktop. */
export const shareOrDownloadBlob = async ({
  blob,
  fileName,
  title,
  mediaType,
}: {
  blob: Blob;
  fileName: string;
  title: string;
  mediaType: string;
}): Promise<ShareReceiptResult> => {
  const file = new File([blob], fileName, { type: mediaType });

  if (shouldOfferNativeShare(mediaType) && typeof navigator.share === "function") {
    try {
      await navigator.share({ files: [file], title });
      return "shared";
    } catch (err) {
      if (isAbortError(err)) return "cancelled";
    }
  }

  downloadBlob(blob, fileName);
  return "downloaded";
};
